/**
 * BLRRD paint engine — Clone (stamp from source point) (Phase 4.C.paint.2,
 * Wave 9.2).
 *
 * Clone samples pixels from a source region at `(target + sourceOffset)`
 * and blends them into the target through the brush mask. The source offset
 * is fixed when the stroke starts (Alt-click sets the source point; the
 * offset = source − first-dab-position) and stays constant for the whole
 * stroke ("aligned" mode — dossier §2.4), so dragging pulls a translated
 * copy of the source area.
 *
 * Sampling is bilinear (Wolberg 1990 §3.4) so sub-pixel source offsets
 * don't quantise; off-source samples clamp to the nearest edge pixel
 * (replicate) rather than producing transparent halos or NaN.
 *
 * v1: same-layer source only (the source buffer IS the active layer's
 * rgba). Cross-layer / pattern sources are a Wave-9.4 follow-up.
 *
 * Provenance (clean-room): Wolberg 1990 "Digital Image Warping" §3.4
 * (bilinear interpolation), Porter-Duff 1984 (over). NO GIMP/GEGL source.
 *
 * @module src/pipeline/paint/clone
 */

import { dabBounds, boundsToRegion, worldToMaskCell, sampleMaskBilinear, ALPHA_EPS } from './brush-ops.js';

/**
 * Bilinear-sample a Float32 RGBA buffer at fractional (sx, sy). Out-of-
 * bounds coordinates clamp to the edge (replicate). Returns [r,g,b,a].
 *
 * @param {Float32Array} rgba
 * @param {number} w
 * @param {number} h
 * @param {number} sx
 * @param {number} sy
 * @returns {[number,number,number,number]}
 */
export function bilinearSampleRGBA(rgba, w, h, sx, sy) {
    /* Clamp into [0, dim-1] so edge samples replicate (no transparent
     * halo at the source boundary — dossier §2.4 source-clipping note). */
    let fx = sx < 0 ? 0 : sx > w - 1 ? w - 1 : sx;
    let fy = sy < 0 ? 0 : sy > h - 1 ? h - 1 : sy;
    const x0 = Math.floor(fx);
    const y0 = Math.floor(fy);
    const x1 = x0 + 1 < w ? x0 + 1 : x0;
    const y1 = y0 + 1 < h ? y0 + 1 : y0;
    const tx = fx - x0;
    const ty = fy - y0;
    const i00 = (y0 * w + x0) * 4;
    const i10 = (y0 * w + x1) * 4;
    const i01 = (y1 * w + x0) * 4;
    const i11 = (y1 * w + x1) * 4;
    const out = [0, 0, 0, 0];
    for (let c = 0; c < 4; c++) {
        const top = rgba[i00 + c] + (rgba[i10 + c] - rgba[i00 + c]) * tx;
        const bot = rgba[i01 + c] + (rgba[i11 + c] - rgba[i01 + c]) * tx;
        out[c] = top + (bot - top) * ty;
    }
    return out;
}

/**
 * Clone dab — sample source at (cx + offsetX, cy + offsetY) per pixel,
 * blend through the brush mask with Porter-Duff "over".
 *
 * @param {Float32Array} rgba    active-layer buffer (mutated in place)
 * @param {number} w
 * @param {number} h
 * @param {number} cx           dab centre x
 * @param {number} cy           dab centre y
 * @param {object} brush
 * @param {Float32Array} brush.mask
 * @param {number} brush.maskSize
 * @param {number} brush.effSize
 * @param {number} brush.flow
 * @param {Float32Array} brush.source   source buffer to sample from
 * @param {number} brush.sourceW
 * @param {number} brush.sourceH
 * @param {number} brush.offsetX  source = target + offset
 * @param {number} brush.offsetY
 * @returns {{x:number,y:number,w:number,h:number}|null} dirty region
 */
export function stampCloneDab(rgba, w, h, cx, cy, brush) {
    const { mask, maskSize, effSize, flow, source, sourceW, sourceH, offsetX, offsetY } = brush;
    if (!source) return null;
    const b = dabBounds(cx, cy, effSize, w, h);
    if (!b) return null;
    const f = flow == null ? 1 : (flow < 0 ? 0 : flow > 1 ? 1 : flow);
    if (f <= 0) return null;
    const r = effSize / 2;
    const ox = cx - r, oy = cy - r;
    const sW = sourceW || w, sH = sourceH || h;

    for (let y = b.y0; y <= b.y1; y++) {
        const my = worldToMaskCell(y + 0.5, oy, effSize, maskSize);
        const row = y * w * 4;
        for (let x = b.x0; x <= b.x1; x++) {
            const mx = worldToMaskCell(x + 0.5, ox, effSize, maskSize);
            const cov = sampleMaskBilinear(mask, maskSize, mx, my);
            if (cov <= 0) continue;
            const as = cov * f;
            if (as <= 0) continue;
            /* Sample source at the offset position (sub-pixel bilinear). */
            const sample = bilinearSampleRGBA(source, sW, sH, x + offsetX, y + offsetY);
            const sr = sample[0], sg = sample[1], sb = sample[2], sa = sample[3];
            /* Combine the dab coverage with the source's own alpha so
             * cloning from a transparent source region doesn't paint
             * opaque pixels. */
            const asEff = as * (sa < 0 ? 0 : sa > 1 ? 1 : sa);
            if (asEff <= 0) continue;
            const j = row + x * 4;
            const dr = rgba[j], dg = rgba[j + 1], db = rgba[j + 2], ad = rgba[j + 3];
            const ao = asEff + ad * (1 - asEff);
            if (ao <= ALPHA_EPS) {
                rgba[j] = 0; rgba[j + 1] = 0; rgba[j + 2] = 0; rgba[j + 3] = 0;
                continue;
            }
            const invAo = 1 / ao;
            rgba[j]     = (sr * asEff + dr * ad * (1 - asEff)) * invAo;
            rgba[j + 1] = (sg * asEff + dg * ad * (1 - asEff)) * invAo;
            rgba[j + 2] = (sb * asEff + db * ad * (1 - asEff)) * invAo;
            rgba[j + 3] = ao;
        }
    }
    return boundsToRegion(b);
}
